import React from 'react'

import { Box, Text, Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import {
  CreatePerformanceReviewProcessDocument,
  CreatePerformanceReviewProcessFragment,
  CreatePerformanceReviewProcessMutationVariables,
} from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CycleSelectField } from 'modules/performance_cycles/components/CycleSelectField'
import {
  type ScoreMode,
  ScoreModeSelector,
} from 'modules/performance_process/components/ScoreModeManager/ScoreModeSelector'
import ReminderNotificationCard from 'modules/performance_process/components/common/ReminderNotificationCard'
import { DeadlinePicker } from 'modules/performance_process/components/review/forms/Deadline'
import { ReviewerTypeSection } from 'modules/performance_process/components/review/forms/ReviewerType'
import { TargetGroupSection } from 'modules/performance_process/components/review/forms/TargetGroup'
import { SettingsToggle } from 'modules/performance_process/components/review/settings/SettingsToggle'
import {
  scoreConfigurationToEvent,
  targetStrategyToEvent,
} from 'modules/performance_process/lib/events'

type WizardData = CreatePerformanceReviewProcessMutationVariables
interface BasicInfoStepProps extends WizardStepRenderParams<WizardData> {
  hasGoalsConnectionFeature: boolean
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  data,
  onDataChanged,
  hasGoalsConnectionFeature,
}) => (
  <Box gap="s32">
    <Text.Normal>
      {i18n.t('performance_process.reviews.basic_information_section.title')}
    </Text.Normal>
    <Box gap="s12" paddingRight="s6">
      <TextField
        mandatory
        value={data.name || undefined}
        label={i18n.t('performance_process.reviews.basic_information_section.title_label')}
        placeholder={i18n.t(
          'performance_process.reviews.basic_information_section.title_placeholder'
        )}
        onChange={(name) => onDataChanged({ ...data, name })}
      />
      <TextArea
        value={data.description}
        minRows={8}
        label={i18n.t('performance_process.reviews.basic_information_section.description_label')}
        placeholder={i18n.t(
          'performance_process.reviews.basic_information_section.description_placeholder_new_review'
        )}
        onChange={(description) => onDataChanged({ ...data, description })}
      />
      {hasGoalsConnectionFeature && (
        <CycleSelectField
          value={data.cycleId}
          label={i18n.t('performance_process.reviews.basic_information_section.cycle_label')}
          blankLabel={i18n.t(
            'performance_process.reviews.basic_information_section.cycle_blank_label'
          )}
          placeholder={i18n.t(
            'performance_process.reviews.basic_information_section.cycle_placeholder'
          )}
          onChange={(cycleId) => onDataChanged({ ...data, cycleId })}
        />
      )}
    </Box>
  </Box>
)

const ReviewerTypeStep: React.FC<WizardStepRenderParams<WizardData>> = ({
  data,
  onDataChanged,
}) => (
  <Box gap="s32">
    <Text.Normal>
      {i18n.t('performance_process.reviews.create.reviewer_type_explanation_new')}
    </Text.Normal>
    <ReviewerTypeSection
      reviewerStrategies={data.reviewerStrategies || []}
      disabled={false}
      onChange={(reviewerStrategies) =>
        onDataChanged({
          ...data,
          reviewerStrategies: reviewerStrategies || [],
        })
      }
    />
  </Box>
)

const TargetGroupStep: React.FC<WizardStepRenderParams<WizardData>> = ({ data, onDataChanged }) => (
  <Box gap="s32">
    <Text.Normal>
      {i18n.t('performance_process.reviews.create.target_group_explanation_new')}
    </Text.Normal>
    <TargetGroupSection
      showEmpty
      variables={data}
      onChange={(variables) => {
        if (variables.targetStrategy) {
          onDataChanged({
            ...data,
            targetStrategy: variables.targetStrategy,
            arguments: variables.arguments || undefined,
          })
        }
      }}
    />
  </Box>
)

interface PerformanceScoringSystemStepProps extends WizardStepRenderParams<WizardData> {
  hasCustomScoreConfigurationEnabled: boolean
}

const ScoringSystemStep: React.FC<PerformanceScoringSystemStepProps> = ({
  data,
  onDataChanged,
  hasCustomScoreConfigurationEnabled,
}) => {
  const finalQuestion = data.employeeScoreEnabled ? 'final_question' : null
  const customWeights = data.customSectionWeightsEnabled ? 'custom_weights' : null

  const handleFeatureToggle = (mode: ScoreMode) => {
    onDataChanged({
      ...data,
      employeeScoreEnabled: mode == 'final_question',
      customSectionWeightsEnabled: mode == 'custom_weights',
    })
  }

  return (
    <ScoreModeSelector
      mode={finalQuestion || customWeights}
      onChange={handleFeatureToggle}
      disabled={false}
      hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
    />
  )
}

interface OtherSettingsStepProps extends WizardStepRenderParams<WizardData> {
  isRepublishFeatureEnabled: boolean
}
const OtherSettingsStep: React.FC<OtherSettingsStepProps> = ({
  data,
  onDataChanged,
  isRepublishFeatureEnabled,
}) => (
  <Box gap="s32">
    <Text.Normal>{i18n.t('performance_process.reviews.create.features_explanation')}</Text.Normal>

    <Box gap="s24">
      <SettingsToggle
        title={i18n.t('performance_process.agreements.settings.title')}
        description={i18n.t('performance_process.agreements.settings.description')}
        onChange={(value) => onDataChanged({ ...data, agreementsEnabled: value })}
        value={data.agreementsEnabled}
      />
      <SettingsToggle
        title={i18n.t('performance_process.competency_assessment.settings.title')}
        description={i18n.t('performance_process.competency_assessment.settings.description')}
        onChange={(value) => onDataChanged({ ...data, competenciesAssessmentsEnabled: value })}
        value={data.competenciesAssessmentsEnabled}
      />
      <SettingsToggle
        title={i18n.t('performance_process.employee_potential_score.settings.title')}
        description={i18n.t('performance_process.employee_potential_score.settings.description')}
        onChange={(value) => onDataChanged({ ...data, employeePotentialScoreEnabled: value })}
        value={data.employeePotentialScoreEnabled}
        infoCalloutDescription={i18n.t(
          'performance_process.employee_potential_score.settings.info_callout_description'
        )}
      />
      {isRepublishFeatureEnabled && (
        <SettingsToggle
          title={i18n.t('performance_process.republish_evaluations.settings.title')}
          description={i18n.t('performance_process.republish_evaluations.settings.description')}
          onChange={(value) => onDataChanged({ ...data, republishEvaluationsEnabled: value })}
          checkboxAriaLabel={i18n.t(
            'performance_process.republish_evaluations.settings.toggle_checkbox_label'
          )}
          value={data.republishEvaluationsEnabled}
        />
      )}
    </Box>
  </Box>
)

const DeadlineStep: React.FC<WizardStepRenderParams<WizardData>> = ({ data, onDataChanged }) => {
  return (
    <Box gap="s32">
      <Box gap="s16">
        <Text.Normal>
          {i18n.t('performance_process.reviews.create.deadline_explanation_new')}
        </Text.Normal>
        <DeadlinePicker
          date={data.endsAt}
          onChange={(date) => {
            onDataChanged({
              ...data,
              endsAt: date,
            })
          }}
          restrictToFutureTimes
        />
      </Box>

      <ReminderNotificationCard endsAt={data.endsAt} />
    </Box>
  )
}

interface Props {
  authorAccessId: number
  hasCustomScoreConfigurationEnabled?: boolean
  hasGoalsConnectionFeature?: boolean
  isRepublishFeatureEnabled: boolean
  onClose?: () => void
  onSubmit?: (data: CreatePerformanceReviewProcessFragment) => void
}

export const CreateProcessModal: React.FC<Props> = ({
  authorAccessId,
  hasCustomScoreConfigurationEnabled = false,
  hasGoalsConnectionFeature = false,
  onSubmit,
  onClose,
  isRepublishFeatureEnabled,
}) => {
  const [submitting, setSubmitting] = React.useState(false)

  const [createProcess] = useMutation(CreatePerformanceReviewProcessDocument, {
    extractorFn: (data) => data.performanceMutations.createPerformanceReviewProcess,
  })

  const Modal = Wizard.Modal<WizardData>

  const handleSubmit = async (data: WizardData) => {
    if (submitting) return

    setSubmitting(true)

    const result = await createProcess({ variables: data })
    const handled = handleResult(result, (errors) => {
      const messages = errors.messages
      if (messages) {
        app.setFlash({
          type: 'wrong',
          message: messages ?? i18n.t('common.error'),
        })
      }
    })

    setSubmitting(false)
    if (!handled.success) return

    const reviewProcess = handled.data?.reviewProcess
    if (!reviewProcess) return

    track(trackingEvents.performanceProcess.createReviewSubmitted, {
      review_type: 'blank',
      reviewer_strategies: reviewProcess.reviewerStrategies,
      reviewees: targetStrategyToEvent(reviewProcess.targetStrategy),
      performance_score: scoreConfigurationToEvent(reviewProcess.scoreConfiguration),
    })
    onSubmit?.(reviewProcess)
  }

  if (!open) return null

  return (
    <Modal
      initialData={{
        name: '',
        description: '',
        cycleId: null,
        authorAccessId: authorAccessId,
        targetStrategy: null,
        reviewerStrategies: [],
        endsAt: null,
        agreementsEnabled: true,
        employeeScoreEnabled: true,
        employeePotentialScoreEnabled: false,
        customSectionWeightsEnabled: false,
        competenciesAssessmentsEnabled: false,
        republishEvaluationsEnabled: false,
      }}
      icon="add-block"
      title={i18n.t('performance_process.reviews.create.new_review')}
      description={i18n.t('performance_process.reviews.create.description')}
      onClose={() => onClose?.()}
      onSubmit={handleSubmit}
      steps={[
        {
          title: i18n.t('performance_process.reviews.create.basic_information'),
          nextText: i18n.t('performance_process.reviews.create.continue_button'),
          render: (props) => (
            <BasicInfoStep {...props} hasGoalsConnectionFeature={hasGoalsConnectionFeature} />
          ),
          isCompleted: (data) => !!data.name,
        },
        {
          title: i18n.t('performance_process.reviews.create.target_settings'),
          nextText: i18n.t('performance_process.reviews.create.continue_button'),
          render: (props) => <ReviewerTypeStep {...props} />,
          isCompleted: (_data) => true,
        },
        {
          title: i18n.t('performance_process.reviews.create.participants'),
          nextText: i18n.t('performance_process.reviews.create.continue_button'),
          render: (props) => <TargetGroupStep {...props} />,
          isCompleted: (_data) => true,
        },
        {
          title: i18n.t('performance_process.reviews.scoring_system.title'),
          nextText: i18n.t('performance_process.reviews.create.continue_button'),
          render: (props) => (
            <ScoringSystemStep
              {...props}
              hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
            />
          ),
          isCompleted: (_data) => true,
        },
        {
          title: i18n.t('performance_process.reviews.create.features'),
          nextText: i18n.t('performance_process.reviews.create.continue_button'),
          render: (props) => (
            <OtherSettingsStep {...props} isRepublishFeatureEnabled={isRepublishFeatureEnabled} />
          ),
          isCompleted: (_data) => true,
        },
        {
          title: i18n.t('performance_process.reviews.timeline'),
          nextText: i18n.t('performance_process.reviews.create.create_review_button'),
          render: (props) => <DeadlineStep {...props} />,
          isCompleted: (_data) => true,
        },
      ]}
    />
  )
}
